import { useState, useCallback, useEffect } from 'react'
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  LayoutDashboard, Inbox, TrendingUp, PenSquare,
  Megaphone, FlaskConical, Bot, Settings, CalendarDays,
  Shield, FileBarChart, ShieldAlert, Webhook, DollarSign, ShieldCheck,
  Menu, X, LogOut,
} from 'lucide-react'
import logoSrc from './assets/logo.png'
import clsx from 'clsx'

// ── Views ────────────────────────────────────────────────────────────────────
import DashboardView from './views/DashboardView'
import InboxView from './views/InboxView'
import TrendsView from './views/TrendsView'
import ContentView from './views/ContentView'
import CalendarView from './views/CalendarView'
import AdsView from './views/AdsView'
import ExperimentsView from './views/ExperimentsView'
import AgentsView from './views/AgentsView'
import GovernanceView from './views/GovernanceView'
import ReportBuilderView from './views/ReportBuilderView'
import SettingsView from './views/SettingsView'
import CrisisView from './views/CrisisView'
import WebhooksView from './views/WebhooksView'
import BudgetView from './views/BudgetView'
import ComplianceView from './views/ComplianceView'
import OAuthCallbackView from './views/OAuthCallbackView'
import LoginView from './views/LoginView'
import RegisterView from './views/RegisterView'
import PrivacyPolicyView, { DataDeletionView, TermsConditionsView } from './views/PrivacyPolicyView'

// ── Shared components ────────────────────────────────────────────────────────
import SkipNav from './components/SkipNav'
import ErrorBoundary from './components/ErrorBoundary'
import { ToastProvider } from './components/Toast'
import { ConfirmProvider } from './components/ConfirmDialog'
import ConnectionStatus from './components/ConnectionStatus'

// ── WebSocket real-time hook ────────────────────────────────────────────────
import { useWebSocket, type WsEvent } from './hooks/useWebSocket'

// ── API client ──────────────────────────────────────────────────────────────
import {
  clearAuthToken,
  currentSession,
  getApiBase,
  getAuthChangedEventName,
  getAuthToken,
  logoutSession,
} from './api/client'
import {
  setClarityMarketingContext,
  setClarityRoute,
  setClarityUserContext,
  trackClarityEvent,
} from './analytics/clarity'

const NAV = [
  { to: '/',            icon: LayoutDashboard, label: 'Overview'    },
  { to: '/inbox',       icon: Inbox,           label: 'Inbox'       },
  { to: '/trends',      icon: TrendingUp,      label: 'Trends'      },
  { to: '/content',     icon: PenSquare,       label: 'Content'     },
  { to: '/calendar',    icon: CalendarDays,    label: 'Calendar'    },
  { to: '/ads',         icon: Megaphone,       label: 'Ads'         },
  { to: '/experiments', icon: FlaskConical,    label: 'Experiments' },
  { to: '/agents',      icon: Bot,             label: 'Agents'      },
  { to: '/governance',  icon: Shield,          label: 'Governance'  },
  { to: '/crisis',      icon: ShieldAlert,     label: 'Crisis'      },
  { to: '/budget',      icon: DollarSign,      label: 'Budget'      },
  { to: '/compliance',  icon: ShieldCheck,     label: 'Compliance'  },
  { to: '/webhooks',    icon: Webhook,         label: 'Webhooks'    },
  { to: '/reports',     icon: FileBarChart,    label: 'Reports'     },
  { to: '/settings',    icon: Settings,        label: 'Settings'    },
]

/** Derive a short display label from the full API base URL. */
function apiDisplayLabel(): string {
  try {
    const url = new URL(getApiBase())
    return `${url.host}${url.pathname}`
  } catch {
    // If the base is a relative path like "/api/v1", show origin + path
    return `${window.location.host}/api/v1`
  }
}

function Sidebar({
  isOpen,
  onClose,
  onLogout,
  userLabel,
}: {
  isOpen: boolean
  onClose: () => void
  onLogout: () => void
  userLabel: string
}) {
  return (
    <>
      {/* Mobile backdrop overlay — dims main content when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/*
        Mobile:  fixed drawer that slides in from the left
        Desktop: sticky sidebar always visible in the flex flow
      */}
      <aside
        id="primary-sidebar"
        className={clsx(
          // Dimensions & layout
          'w-56 h-screen flex flex-col border-r border-surface-border bg-surface',
          // Mobile: fixed drawer, sits above overlay (z-50 > z-40)
          'fixed top-0 left-0 z-50',
          // Desktop: back into flex flow, no fixed stacking context needed
          'lg:sticky lg:shrink-0 lg:z-auto',
          // Slide animation — disabled on desktop for instant appearance
          'transition-transform duration-300 ease-in-out lg:transition-none',
          // Visibility: hidden off-screen on mobile unless open; always shown on desktop
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        aria-label="Primary navigation"
      >
        {/* Logo */}
        <div className="px-4 py-5 flex items-center gap-2 border-b border-surface-border">
          <img src={logoSrc} alt="SocialScale Logo" className="w-5 h-5 object-contain" />
          <span className="font-semibold text-white tracking-tight">SocialScale</span>
          <span
            className="ml-auto text-[10px] bg-brand-700 text-brand-100 px-1.5 py-0.5 rounded-full"
            aria-label="Version 0.2"
          >
            v0.2
          </span>
          {/* Close button — mobile only */}
          <button
            onClick={onClose}
            className="lg:hidden ml-2 p-1 rounded hover:bg-slate-800 transition-colors"
            aria-label="Close navigation"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Nav -- ARIA landmark */}
        <nav
          className="flex-1 min-h-0 py-3 px-2 space-y-0.5 overflow-y-auto"
          aria-label="Main menu"
        >
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={onClose}
              aria-label={label}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-1',
                  isActive
                    ? 'bg-brand-700/40 text-brand-200 font-medium'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                )
              }
            >
              <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Footer -- status section */}
        <div
          className="px-4 py-3 border-t border-surface-border text-xs text-slate-500 space-y-1.5"
          role="status"
          aria-live="polite"
          aria-label="System status"
        >
          <div className="text-slate-400 truncate" title={userLabel}>
            Signed in as: {userLabel}
          </div>
          <div>
            Autonomous mode:{' '}
            <span className="text-yellow-400" aria-label="Copilot mode active">Copilot</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">API:</span>
            <span className="text-slate-400 truncate" title={getApiBase()}>
              {apiDisplayLabel()}
            </span>
          </div>
          {/* WebSocket connection badge */}
          <ConnectionStatus />
          <button
            onClick={onLogout}
            className="mt-1 w-full h-8 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  )
}

export default function App() {
  const location = useLocation()
  const qc = useQueryClient()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [authReady, setAuthReady] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userLabel, setUserLabel] = useState('Operator')
  const [userId, setUserId] = useState<string | null>(null)
  const [userRole, setUserRole] = useState<string | null>(null)

  // ── Real-time: invalidate React Query caches when the server broadcasts ───
  const handleWsEvent = useCallback((event: WsEvent) => {
    switch (event.channel) {
      case 'inbox':
        void qc.invalidateQueries({ queryKey: ['threads'] })
        break
      case 'calendar':
        void qc.invalidateQueries({ queryKey: ['drafts'] })
        void qc.invalidateQueries({ queryKey: ['calendar'] })
        break
      case 'analytics':
        void qc.invalidateQueries({ queryKey: ['analytics'] })
        break
      case 'agents':
        void qc.invalidateQueries({ queryKey: ['agents'] })
        break
      default:
        break
    }
  }, [qc])

  // Connect WebSocket for real-time updates -- auto-reconnects on disconnect
  useWebSocket(handleWsEvent)

  // ── Session bootstrap / auth guard state ────────────────────────────────
  useEffect(() => {
    let cancelled = false

    async function syncAuthState() {
      const token = getAuthToken()
      if (!token) {
        if (!cancelled) {
          setIsAuthenticated(false)
          setUserLabel('Operator')
          setUserId(null)
          setUserRole(null)
          setAuthReady(true)
        }
        return
      }

      try {
        const me = await currentSession()
        if (cancelled) return
        if (me.authenticated) {
          setIsAuthenticated(true)
          const fallbackName = me.user?.email ?? 'Operator'
          setUserLabel(me.user?.name?.trim() || fallbackName)
          setUserId(me.user?.id ?? null)
          setUserRole(me.role || null)
        } else {
          clearAuthToken()
          setIsAuthenticated(false)
          setUserLabel('Operator')
          setUserId(null)
          setUserRole(null)
        }
      } catch {
        clearAuthToken()
        if (!cancelled) {
          setIsAuthenticated(false)
          setUserLabel('Operator')
          setUserId(null)
          setUserRole(null)
        }
      } finally {
        if (!cancelled) setAuthReady(true)
      }
    }

    void syncAuthState()

    const onAuthChange = () => {
      setAuthReady(false)
      void syncAuthState()
    }

    window.addEventListener(getAuthChangedEventName(), onAuthChange)
    return () => {
      cancelled = true
      window.removeEventListener(getAuthChangedEventName(), onAuthChange)
    }
  }, [])

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}${location.hash}`
    setClarityRoute(fullPath)
    setClarityMarketingContext(location.search)
  }, [location.hash, location.pathname, location.search])

  useEffect(() => {
    setClarityUserContext({
      authenticated: isAuthenticated,
      role: userRole,
      userId,
    })
    if (authReady) {
      trackClarityEvent(isAuthenticated ? 'session_authenticated' : 'session_anonymous')
    }
  }, [authReady, isAuthenticated, userId, userRole])

  // ── Body scroll lock: prevents the background from scrolling while the
  //    mobile sidebar drawer is open — standard UX for all professional apps ──
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [sidebarOpen])

  // ── Escape key closes the sidebar (accessibility + power-user UX) ──────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) setSidebarOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [sidebarOpen])

  async function handleLogout() {
    try {
      await logoutSession()
    } catch {
      // Token is cleared even if API logout fails to keep UX deterministic.
    } finally {
      clearAuthToken()
      setSidebarOpen(false)
      setIsAuthenticated(false)
      setUserLabel('Operator')
      setUserId(null)
      setUserRole(null)
    }
  }

  const loadingGate = (
    <div className="min-h-screen bg-surface grid place-items-center px-4">
      <div className="w-full max-w-sm rounded-xl border border-surface-border bg-surface-card p-5 text-center">
        <div className="inline-flex items-center gap-2 text-brand-300 font-medium">
          <img src={logoSrc} alt="Logo" className="w-4 h-4 object-contain" />
          Securing your workspace...
        </div>
      </div>
    </div>
  )

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ConfirmProvider>
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicyView />} />
            <Route path="/data-deletion" element={<DataDeletionView />} />
            <Route path="/terms" element={<TermsConditionsView />} />
            <Route
              path="/login"
              element={
                authReady && isAuthenticated
                  ? <Navigate to="/" replace />
                  : <LoginView />
              }
            />
            <Route
              path="/register"
              element={
                authReady && isAuthenticated
                  ? <Navigate to="/" replace />
                  : <RegisterView />
              }
            />
            <Route
              path="/oauth/callback/:platform"
              element={isAuthenticated ? <OAuthCallbackView /> : <Navigate to="/login" replace />}
            />
            <Route
              path="*"
              element={
                !authReady ? loadingGate : (
                  !isAuthenticated ? (
                    <Navigate to="/login" replace />
                  ) : (
                    <div className="flex min-h-screen bg-surface">
                      {/* WCAG 2.4.1 -- Skip navigation link */}
                      <SkipNav targetId="main-content" />

                      {/*
                        Mobile top header bar (hidden on lg+).
                        Contains hamburger button + logo — mirrors the sidebar header.
                        Fixed at the top so it stays visible while the main content scrolls.
                      */}
                      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 z-30 bg-surface border-b border-surface-border flex items-center px-4 gap-3">
                        {/* Hamburger */}
                        <button
                          onClick={() => setSidebarOpen(true)}
                          className="p-2 rounded-lg hover:bg-slate-800 transition-colors shrink-0"
                          aria-label="Open navigation"
                          aria-expanded={sidebarOpen}
                          aria-controls="primary-sidebar"
                        >
                          <Menu className="w-5 h-5 text-slate-400" />
                        </button>

                        {/* Logo */}
                        <div className="flex items-center gap-1.5 min-w-0">
                          <img src={logoSrc} alt="SocialScale Logo" className="w-4 h-4 shrink-0 object-contain" />
                          <span className="font-semibold text-white text-sm tracking-tight truncate">SocialScale</span>
                          <span className="text-[10px] bg-brand-700 text-brand-100 px-1.5 py-0.5 rounded-full shrink-0">v0.2</span>
                        </div>

                        {/* Connection status pill — pushed to the right */}
                        <div className="ml-auto shrink-0">
                          <ConnectionStatus />
                        </div>
                      </div>

                      <Sidebar
                        isOpen={sidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                        onLogout={handleLogout}
                        userLabel={userLabel}
                      />

                      <main
                        id="main-content"
                        className="flex-1 overflow-auto pt-14 lg:pt-0"
                        tabIndex={-1}
                        aria-label="Main content"
                      >
                        <Routes>
                          <Route path="/"            element={<DashboardView />} />
                          <Route path="/inbox"       element={<InboxView />} />
                          <Route path="/trends"      element={<TrendsView />} />
                          <Route path="/content"     element={<ContentView />} />
                          <Route path="/calendar"    element={<CalendarView />} />
                          <Route path="/ads"         element={<AdsView />} />
                          <Route path="/experiments" element={<ExperimentsView />} />
                          <Route path="/agents"      element={<AgentsView />} />
                          <Route path="/governance"  element={<GovernanceView />} />
                          <Route path="/crisis"      element={<CrisisView />} />
                          <Route path="/budget"      element={<BudgetView />} />
                          <Route path="/compliance"  element={<ComplianceView />} />
                          <Route path="/webhooks"    element={<WebhooksView />} />
                          <Route path="/reports"     element={<ReportBuilderView />} />
                          <Route path="/settings"    element={<SettingsView />} />
                          <Route path="*"            element={<Navigate to="/" replace />} />
                        </Routes>
                      </main>
                    </div>
                  )
                )
              }
            />
          </Routes>
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}
